import { Cli } from "incur";
import { nativeSpec, filterSpecByTag, createWhopFetch } from "./api";
import { API_GROUPS } from "./api/groups";
import { buildAppGroup } from "./apps/commands";
import {
	buildAuthGroup,
	logoutAdapter,
	logoutOptions,
	logoutOutput,
} from "./auth/commands";
import {
	loginAdapter,
	loginEnv,
	loginExamples,
	loginHint,
	loginOptions,
	loginOutput,
} from "./auth/login";
import { buildMediaGroup } from "./media/commands";
import pkg from "../package.json" with { type: "json" };
import {
	quickstartOptions,
	quickstartOutput,
	runQuickstart,
} from "./journey/quickstart";
import { render } from "./lib/renderer";
import {
	runUpgrade,
	upgradeOptions,
	upgradeOutput,
} from "./lib/update/command";
import { performLogin } from "./auth/login";
import { getActiveProfile } from "./auth/profiles";
import { PromptCancelledError } from "./auth/prompts";

const cli = Cli.create("whop", {
	version: pkg.version,
	description: "Build and manage Whop apps from your terminal.",
	renderer: render,
	sync: {
		// Shipped skill files generated by scripts/sync-spec.ts from the
		// spec's own tag copy (x-whop-summary + description, authored in
		// backend/openapi/v1/tags.yml). A matching directory name overrides
		// the auto-generated skill for that command group.
		include: ["skills/*"],
		suggestions: [
			"choose or create my Whop business account",
			"show my Whop business accounts",
			"create a Whop product and pricing plan",
		],
	},
	mcp: {
		agents: ["claude-desktop"],
	},
});

const spec = (tag: string) =>
	filterSpecByTag(nativeSpec as Record<string, unknown>, tag);
const fetch = createWhopFetch();

const isAuthExempt = (cmd: string) =>
	!cmd ||
	cmd === "login" ||
	cmd === "logout" ||
	cmd === "completions" ||
	cmd === "upgrade" ||
	cmd.startsWith("auth") ||
	cmd.startsWith("mcp");

cli.use(async (c, next) => {
	if (isAuthExempt(c.command)) return next();
	if (getActiveProfile()) return next();
	if (process.env.WHOP_API_KEY) return next();

	if (c.agent || c.formatExplicit) {
		return c.error({
			code: "NOT_AUTHENTICATED",
			message: "Not logged in. Set WHOP_API_KEY or run `whop login` first.",
			cta: {
				commands: [{ command: "auth login", description: "Log in to Whop" }],
			},
		});
	}

	let result;
	try {
		result = await performLogin({
			agent: false,
			deferAccountSelection: c.command === "quickstart",
		});
	} catch (err) {
		if (err instanceof PromptCancelledError) {
			return c.error({
				code: "CANCELLED",
				message: "Login cancelled.",
				exitCode: 130,
			});
		}
		throw err;
	}

	if (!result.ok) {
		return c.error({
			code: result.code,
			message: result.message,
			cta: {
				commands: [{ command: "auth login", description: "Log in to Whop" }],
			},
		});
	}

	return next();
});

cli.command("quickstart", {
	description: "Set up the business account the CLI should use",
	hint: "OAuth users can choose or create a business. API keys are tied to one business. Agents and scripts should pass --account_id or --business_name when the choice is ambiguous.",
	options: quickstartOptions,
	output: quickstartOutput,
	examples: [
		{ description: "Choose or create a business interactively" },
		{
			options: { account_id: "biz_xxx" },
			description: "Choose an existing business account",
		},
		{
			options: { business_name: "Acme" },
			description: "Create and select a new business account",
		},
	],
	outputPolicy: "agent-only",
	run: (c) => runQuickstart(c),
});

cli.command(await buildAppGroup());

cli.command("upgrade", {
	description: "Update the whop CLI to the latest version",
	options: upgradeOptions,
	output: upgradeOutput,
	examples: [
		{ description: "Update to the latest version" },
		{
			options: { check: true },
			description: "Check for a newer version without installing",
		},
	],
	run: (c) => runUpgrade(c),
});

cli.command(buildAuthGroup());

cli.command("login", {
	description: "Log in to Whop (alias for `auth login`)",
	options: loginOptions,
	env: loginEnv,
	output: loginOutput,
	examples: loginExamples,
	hint: loginHint,
	run: (c) => loginAdapter(c),
});

cli.command("logout", {
	description: "Log out (alias for `auth logout`)",
	options: logoutOptions,
	output: logoutOutput,
	run: (c) => logoutAdapter(c),
});

for (const { name, tag, description } of API_GROUPS) {
	// Media is hybrid: spec commands plus a hand-written `generate --wait`.
	if (name === "media") {
		cli.command(await buildMediaGroup(description));
		continue;
	}
	cli.command(name, {
		description,
		fetch,
		openapi: spec(tag),
		openapiConfig: { security: false },
	});
}

export default cli;
